import BlockIcon from "@mui/icons-material/Block";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import TuneIcon from "@mui/icons-material/Tune";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import page6_section1 from "../../../assets/images/AboutVedaz/page6_section1.jpg";
import page6_section3 from "../../../assets/images/AboutVedaz/page6_section3.jpg";
import page6_section4 from "../../../assets/images/AboutVedaz/page6_section4.jpg";
import page6_section6 from "../../../assets/images/AboutVedaz/page6_section6.jpg";
import page6_section7 from "../../../assets/images/AboutVedaz/page6_section7.jpg";
import classes from "../AboutVedaz.styles";
import { ColorBanner, FeaturePoint } from "./CompatComponents";

interface Props {
  chapterIndex: number;
}

const PhysicalAspect = ({ chapterIndex }: Props) => {
  const { t } = useTranslation();

  return (
    <Box>
      {/* ── Section 1: Background image with glass card ───────────── */}
      <Box sx={classes.bgSection}>
        <Box
          component="img"
          src={page6_section1}
          alt=""
          sx={{ ...classes.bgSectionMedia, objectPosition: "top" }}
        />
        <Box sx={classes.bgSectionOverlay} />
        <Box sx={classes.bgSectionContent}>
          <Typography sx={classes.sectionHeading}>
            {t("about_physical_heading")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, maxWidth: "400px", mb: 0 }}>
            {t("about_physical_s1_body")}
          </Typography>
        </Box>
      </Box>

      {/* ── Section 2: Red — no compatibility ────────────────────── */}
      <Box sx={classes.plainSection}>
        <ColorBanner
          colorKey="red"
          Icon={BlockIcon}
          header={t("about_physical_red_header")}
          subtitle={t("about_physical_red_subtitle")}
        />
        <Typography sx={{ ...classes.bodyText, mb: 3 }}>
          {t("about_physical_red_intro")}
        </Typography>
        <FeaturePoint
          colorKey="red"
          Icon={SentimentDissatisfiedIcon}
          title={t("about_physical_red_p1_title")}
          body={t("about_physical_red_p1_body")}
        />
        <FeaturePoint
          colorKey="red"
          Icon={PsychologyIcon}
          title={t("about_physical_red_p2_title")}
          body={t("about_physical_red_p2_body")}
        />
        <FeaturePoint
          colorKey="red"
          Icon={CompareArrowsIcon}
          title={t("about_physical_red_p3_title")}
          body={t("about_physical_red_p3_body")}
        />
      </Box>

      {/* ── Section 3: Compatibility indicator showcase ───────────── */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: "#060414",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: { xs: 5, sm: 7 },
          minHeight: { xs: "340px", sm: "480px", md: "560px" },
        }}
      >
        {/* Dual-tone ambient background */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: [
              "radial-gradient(ellipse 60% 90% at 12% 50%, rgba(244,67,54,0.14) 0%, transparent 100%)",
              "radial-gradient(ellipse 60% 90% at 88% 50%, rgba(139,195,74,0.14) 0%, transparent 100%)",
            ].join(", "),
            zIndex: 0,
            pointerEvents: "none",
          }}
        />

        {/* Left glowing indicator bar */}
        <Box
          sx={{
            position: "absolute",
            top: "8%", bottom: "8%",
            left: { xs: "10px", sm: "20px", md: "32px" },
            width: "3px",
            borderRadius: "3px",
            background:
              "linear-gradient(to bottom, transparent 0%, #F44336 30%, #F44336 70%, transparent 100%)",
            boxShadow:
              "0 0 8px rgba(244,67,54,0.9), 0 0 20px rgba(244,67,54,0.5), 0 0 40px rgba(244,67,54,0.25)",
            zIndex: 1,
          }}
        />

        {/* Right glowing indicator bar */}
        <Box
          sx={{
            position: "absolute",
            top: "8%", bottom: "8%",
            right: { xs: "10px", sm: "20px", md: "32px" },
            width: "3px",
            borderRadius: "3px",
            background:
              "linear-gradient(to bottom, transparent 0%, #8BC34A 30%, #8BC34A 70%, transparent 100%)",
            boxShadow:
              "0 0 8px rgba(139,195,74,0.9), 0 0 20px rgba(139,195,74,0.5), 0 0 40px rgba(139,195,74,0.25)",
            zIndex: 1,
          }}
        />

        {/* Centered image with left/right edge dissolve */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            width: { xs: "86%", sm: "70%", md: "60%" },
            maxWidth: "700px",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, bottom: 0,
              width: "80px",
              background:
                "linear-gradient(to right, #060414 0%, transparent 100%)",
              zIndex: 3,
              pointerEvents: "none",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0, right: 0, bottom: 0,
              width: "80px",
              background:
                "linear-gradient(to left, #060414 0%, transparent 100%)",
              zIndex: 3,
              pointerEvents: "none",
            },
            "& img": { width: "100%", display: "block" },
          }}
        >
          <img src={page6_section3} alt="" />
        </Box>

        {/* Top vignette */}
        <Box
          sx={{
            position: "absolute",
            top: 0, left: 0, right: 0,
            height: "90px",
            background: "linear-gradient(to bottom, #060414, transparent)",
            zIndex: 4,
            pointerEvents: "none",
          }}
        />

        {/* Bottom vignette */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0, left: 0, right: 0,
            height: "90px",
            background: "linear-gradient(to top, #060414, transparent)",
            zIndex: 4,
            pointerEvents: "none",
          }}
        />
      </Box>

      {/* ── Section 4: Yellow — image right, content left ─────────── */}
      <Box sx={classes.sideRow}>
        <Box sx={classes.sideContent}>
          <ColorBanner
            colorKey="yellow"
            Icon={HeartBrokenIcon}
            header={t("about_physical_yellow_header")}
            subtitle={t("about_physical_yellow_subtitle")}
          />
          <Typography sx={classes.bodyText}>
            {t("about_physical_yellow_body1")}
          </Typography>
          <Typography sx={classes.bodyText}>
            {t("about_physical_yellow_body2")}
          </Typography>
          <Typography sx={classes.bodyText}>
            {t("about_physical_yellow_body3")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, mb: 0 }}>
            {t("about_physical_yellow_body4")}
          </Typography>
        </Box>
        <Box sx={classes.sideImage}>
          <img src={page6_section4} alt={t("about_physical_yellow_header")} />
        </Box>
      </Box>

      {/* ── Section 5: Yellow continued — two feature points ─────── */}
      <Box sx={classes.plainSection}>
        <FeaturePoint
          colorKey="yellow"
          Icon={TuneIcon}
          title={t("about_physical_yellow_p1_title")}
          body={t("about_physical_yellow_p1_body")}
        />
        <FeaturePoint
          colorKey="yellow"
          Icon={HourglassEmptyIcon}
          title={t("about_physical_yellow_p2_title")}
          body={t("about_physical_yellow_p2_body")}
        />
      </Box>

      {/* ── Section 6: Light Green — image left, content right ────── */}
      <Box sx={classes.sideRowReverse}>
        <Box sx={classes.sideContent}>
          <ColorBanner
            colorKey="lightGreen"
            Icon={FavoriteBorderIcon}
            header={t("about_physical_lightGreen_header")}
            subtitle={t("about_physical_lightGreen_subtitle")}
          />
          <Typography sx={classes.bodyText}>
            {t("about_physical_lightGreen_body1")}
          </Typography>
          <Typography sx={classes.bodyText}>
            {t("about_physical_lightGreen_body2")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, mb: 0 }}>
            {t("about_physical_lightGreen_body3")}
          </Typography>
        </Box>
        <Box sx={classes.sideImage}>
          <img
            src={page6_section6}
            alt={t("about_physical_lightGreen_header")}
          />
        </Box>
      </Box>

      {/* ── Section 7: Dark Green — background image ──────────────── */}
      <Box sx={classes.bgSection}>
        <Box
          component="img"
          src={page6_section7}
          alt=""
          sx={classes.bgSectionMedia}
        />
        <Box sx={classes.bgSectionOverlay} />
        <Box sx={classes.bgSectionContent}>
          <ColorBanner
            colorKey="darkGreen"
            Icon={FavoriteIcon}
            header={t("about_physical_darkGreen_header")}
            subtitle={t("about_physical_darkGreen_subtitle")}
          />
          <Typography sx={{ ...classes.bodyText, maxWidth: "400px" }}>
            {t("about_physical_darkGreen_body1")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, maxWidth: "400px" }}>
            {t("about_physical_darkGreen_body2")}
          </Typography>
          <Typography sx={{ ...classes.bodyText, maxWidth: "400px", mb: 0 }}>
            {t("about_physical_darkGreen_body3")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PhysicalAspect;
